import { Vector3 } from 'three';
import { Skeleton } from '../skeleton/core';

// ─── Helpers ────────────────────────────────────────────────────────────

function dotAbs(a: Vector3, b: Vector3): number {
  return (
    a.x * Math.abs(b.x) + a.y * Math.abs(b.y) + a.z * Math.abs(b.z)
  );
}

// ─── Branching point ────────────────────────────────────────────────────

export interface BranchingPoint {
  boneIdx: number;
  childCount: number;
  verticalPosition: number;
  lateralOffset: number;
  hasSymmetricChildren: boolean;
  hasDownwardChildren: boolean;
  hasUpwardChildren: boolean;
  hasLateralChildren: boolean;
  symmetryScore: number;
  symmetricPairs: [number, number][];
}

// ─── Skeleton spatial data ──────────────────────────────────────────────

export class SkeletonSpatialData {
  minBounds = new Vector3();
  maxBounds = new Vector3();

  upAxis = new Vector3(0, 1, 0);
  lateralAxis = new Vector3(1, 0, 0);
  forwardAxis = new Vector3(0, 0, 1);

  centerOfMass = new Vector3();

  totalHeight = 0;

  branchingPoints = new Map<number, BranchingPoint>();
  verticalPositions = new Map<number, number>();
  lateralOffsets = new Map<number, number>();
  mirrorPairs = new Map<number, number>();
  mirrorScores = new Map<number, number>();

  centerX: number | null = null;
  leftIsLowerX?: boolean;

  getVerticalPosition(boneIdx: number): number {
    return this.verticalPositions.get(boneIdx) ?? 0.5;
  }

  getLateralOffset(boneIdx: number): number {
    return this.lateralOffsets.get(boneIdx) ?? 0.0;
  }

  isBranchingPoint(boneIdx: number): boolean {
    return this.branchingPoints.has(boneIdx);
  }

  getBranchingPoint(boneIdx: number): BranchingPoint | null {
    return this.branchingPoints.get(boneIdx) ?? null;
  }

  getMirror(boneIdx: number): number | null {
    return this.mirrorPairs.get(boneIdx) ?? null;
  }

  getMirrorScore(boneIdx: number): number {
    return this.mirrorScores.get(boneIdx) ?? 0.0;
  }

  getSideFromSpatial(
    boneIdx: number,
    skeleton?: Skeleton,
  ): 'Left' | 'Right' | null {
    let rawX: number | undefined;

    if (skeleton) {
      const bone = skeleton.bone(boneIdx);
      rawX = bone.restWorld.elements[12];
    }

    if (rawX === undefined) {
      const offset = this.lateralOffsets.get(boneIdx) ?? 0.0;
      if (Math.abs(offset) < 0.1) return null;
      if (this.leftIsLowerX !== undefined) {
        if (this.leftIsLowerX) {
          return offset < -0.1 ? 'Left' : offset > 0.1 ? 'Right' : null;
        }
        return offset > 0.1 ? 'Left' : offset < -0.1 ? 'Right' : null;
      }
      return offset > 0.1 ? 'Left' : offset < -0.1 ? 'Right' : null;
    }

    const minThreshold = this.totalHeight * 0.02;
    const centerRef = this.centerX ?? 0.0;
    const relativeX = rawX - centerRef;

    if (Math.abs(relativeX) < minThreshold) return null;

    if (this.leftIsLowerX !== undefined) {
      if (this.leftIsLowerX) {
        return relativeX < 0 ? 'Left' : 'Right';
      }
      return relativeX > 0 ? 'Left' : 'Right';
    }

    return relativeX > 0 ? 'Left' : 'Right';
  }
}

// ─── Spatial landmarks ──────────────────────────────────────────────────

export class SpatialLandmarks {
  pelvisCandidates: [number, number][] = [];
  chestCandidates: [number, number][] = [];
  handCandidates: Record<string, [number, number][]> = {
    left: [],
    right: [],
  };

  likelyPelvis: number | null = null;
  likelyChest: number | null = null;
  likelyLeftHand: number | null = null;
  likelyRightHand: number | null = null;

  getBestPelvis(): number | null {
    if (this.likelyPelvis !== null) return this.likelyPelvis;
    return this.pelvisCandidates.length > 0
      ? this.pelvisCandidates[0][0]
      : null;
  }

  getBestChest(): number | null {
    if (this.likelyChest !== null) return this.likelyChest;
    return this.chestCandidates.length > 0
      ? this.chestCandidates[0][0]
      : null;
  }
}

// ─── computeSpatialData ─────────────────────────────────────────────────

export function computeSpatialData(skel: Skeleton): SkeletonSpatialData {
  const data = new SkeletonSpatialData();

  const positions: Vector3[] = [];
  for (const bone of skel.bones) {
    if (!bone.auxiliary) {
      positions.push(new Vector3().setFromMatrixPosition(bone.restWorld));
    }
  }

  if (positions.length === 0) return data;

  const minBounds = new Vector3(Infinity, Infinity, Infinity);
  const maxBounds = new Vector3(-Infinity, -Infinity, -Infinity);
  for (const pos of positions) {
    minBounds.min(pos);
    maxBounds.max(pos);
  }
  data.minBounds.copy(minBounds);
  data.maxBounds.copy(maxBounds);

  const extent = new Vector3().subVectors(maxBounds, minBounds);
  const extentArr = [extent.x, extent.y, extent.z];
  const axisOrder = [0, 1, 2].sort((a, b) => extentArr[b] - extentArr[a]);

  if (axisOrder[0] === 1) {
    data.upAxis.set(0, 1, 0);
    data.lateralAxis.set(1, 0, 0);
    data.forwardAxis.set(0, 0, 1);
  } else if (axisOrder[0] === 2) {
    data.upAxis.set(0, 0, 1);
    data.lateralAxis.set(1, 0, 0);
    data.forwardAxis.set(0, 1, 0);
  } else {
    data.upAxis.set(0, 1, 0);
    data.lateralAxis.set(1, 0, 0);
    data.forwardAxis.set(0, 0, 1);
  }

  data.totalHeight = dotAbs(extent, data.upAxis);

  const weightedSum = new Vector3();
  let totalWeight = 0;
  for (const bone of skel.bones) {
    if (!bone.auxiliary && bone.length > 0) {
      const pos = new Vector3().setFromMatrixPosition(bone.restWorld);
      weightedSum.addScaledVector(pos, bone.length);
      totalWeight += bone.length;
    }
  }

  if (totalWeight > 0) {
    data.centerOfMass.copy(weightedSum.divideScalar(totalWeight));
  } else {
    data.centerOfMass
      .addVectors(data.minBounds, data.maxBounds)
      .multiplyScalar(0.5);
  }

  const heightRange = data.totalHeight > 1e-6 ? data.totalHeight : 1.0;
  const minHeight = data.minBounds.dot(data.upAxis);

  const lateralExtent = dotAbs(extent, data.lateralAxis);
  const lateralRange = lateralExtent > 1e-6 ? lateralExtent / 2 : 1.0;
  const centerLateral = data.centerOfMass.dot(data.lateralAxis);

  for (const bone of skel.bones) {
    const pos = new Vector3().setFromMatrixPosition(bone.restWorld);

    const height = pos.dot(data.upAxis);
    data.verticalPositions.set(
      bone.index,
      (height - minHeight) / heightRange,
    );

    const lateral = pos.dot(data.lateralAxis);
    data.lateralOffsets.set(
      bone.index,
      (lateral - centerLateral) / lateralRange,
    );
  }

  detectBranchingPoints(skel, data);
  buildMirrorPairsTable(skel, data);

  return data;
}

// ─── Branching point detection ──────────────────────────────────────────

function detectBranchingPoints(
  skel: Skeleton,
  data: SkeletonSpatialData,
): void {
  const height = data.totalHeight > 1e-6 ? data.totalHeight : 100.0;
  const directionThreshold = height * 0.03;

  for (const bone of skel.bones) {
    if (bone.auxiliary) continue;

    const nonAuxChildren = bone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );
    if (nonAuxChildren.length < 2) continue;

    const bp: BranchingPoint = {
      boneIdx: bone.index,
      childCount: nonAuxChildren.length,
      verticalPosition: data.getVerticalPosition(bone.index),
      lateralOffset: data.getLateralOffset(bone.index),
      hasSymmetricChildren: false,
      hasDownwardChildren: false,
      hasUpwardChildren: false,
      hasLateralChildren: false,
      symmetryScore: 0,
      symmetricPairs: [],
    };

    const bonePos = new Vector3().setFromMatrixPosition(bone.restWorld);

    for (const childIdx of nonAuxChildren) {
      const child = skel.bone(childIdx);
      const childPos = new Vector3().setFromMatrixPosition(child.restWorld);
      const positionDirection = new Vector3().subVectors(childPos, bonePos);

      const childBoneDir =
        child.length > 0
          ? child.direction.clone().multiplyScalar(child.length)
          : child.direction;

      const posVerticalDot = positionDirection.dot(data.upAxis);
      const boneVerticalDot = childBoneDir.dot(data.upAxis);

      if (
        posVerticalDot < -directionThreshold ||
        boneVerticalDot < -directionThreshold
      ) {
        bp.hasDownwardChildren = true;
      } else if (
        posVerticalDot > directionThreshold ||
        boneVerticalDot > directionThreshold
      ) {
        bp.hasUpwardChildren = true;
      }

      const posLateralDot = Math.abs(positionDirection.dot(data.lateralAxis));
      const boneLateralDot = Math.abs(childBoneDir.dot(data.lateralAxis));
      if (
        posLateralDot > directionThreshold ||
        boneLateralDot > directionThreshold
      ) {
        bp.hasLateralChildren = true;
      }
    }

    const minLat = height * 0.015;
    const symmetricPairs = findSymmetricPairs(
      skel,
      nonAuxChildren,
      data.lateralAxis,
      minLat,
    );
    if (symmetricPairs.length > 0) {
      bp.hasSymmetricChildren = true;
      bp.symmetricPairs = symmetricPairs;
      bp.symmetryScore = computeSymmetryScore(
        skel,
        symmetricPairs[0][0],
        symmetricPairs[0][1],
        data.lateralAxis,
      );
    }

    data.branchingPoints.set(bone.index, bp);
  }
}

// ─── Symmetric pair detection ───────────────────────────────────────────

function findSymmetricPairs(
  skel: Skeleton,
  childIndices: number[],
  lateralAxis: Vector3,
  minLateralOffset = 1.0,
): [number, number][] {
  const pairs: [number, number][] = [];
  const used = new Set<number>();

  for (let i = 0; i < childIndices.length; i++) {
    const idxA = childIndices[i];
    if (used.has(idxA)) continue;

    const posA = new Vector3().setFromMatrixPosition(
      skel.bone(idxA).restWorld,
    );
    const lateralA = posA.dot(lateralAxis);

    if (Math.abs(lateralA) < minLateralOffset) continue;

    let bestMatch: number | null = null;
    let bestScore = 0.0;

    for (let j = i + 1; j < childIndices.length; j++) {
      const idxB = childIndices[j];
      if (used.has(idxB)) continue;

      const posB = new Vector3().setFromMatrixPosition(
        skel.bone(idxB).restWorld,
      );
      const lateralB = posB.dot(lateralAxis);

      if (Math.abs(lateralB) < minLateralOffset) continue;
      if (lateralA * lateralB >= 0) continue;

      const score = computeSymmetryScore(skel, idxA, idxB, lateralAxis);
      if (score > bestScore && score > 0.5) {
        bestScore = score;
        bestMatch = idxB;
      }
    }

    if (bestMatch !== null) {
      pairs.push([idxA, bestMatch]);
      used.add(idxA);
      used.add(bestMatch);
    }
  }

  return pairs;
}

function computeSymmetryScore(
  skel: Skeleton,
  idxA: number,
  idxB: number,
  lateralAxis: Vector3,
): number {
  const boneA = skel.bone(idxA);
  const boneB = skel.bone(idxB);

  const sizeA = boneA.subtreeSize;
  const sizeB = boneB.subtreeSize;
  const maxSize = Math.max(sizeA, sizeB);
  const sizeRatio = maxSize > 0 ? Math.min(sizeA, sizeB) / maxSize : 0.0;

  const depthSc =
    1.0 - Math.min(Math.abs(boneA.depth - boneB.depth) / 3.0, 1.0);

  const maxLen = Math.max(boneA.length, boneB.length);
  const lenRatio =
    maxLen > 1e-6 ? Math.min(boneA.length, boneB.length) / maxLen : 0.0;

  const posA = new Vector3().setFromMatrixPosition(boneA.restWorld);
  const posB = new Vector3().setFromMatrixPosition(boneB.restWorld);

  const lateralAVal = posA.dot(lateralAxis);
  const mirroredA = posA.clone().addScaledVector(lateralAxis, -2 * lateralAVal);

  const mirrorDist = mirroredA.distanceTo(posB);
  const skeletonScale = Math.max(posA.length(), posB.length(), 1.0);
  const mirrorSc = 1.0 - Math.min(mirrorDist / skeletonScale, 1.0);

  const score =
    0.3 * sizeRatio + 0.2 * depthSc + 0.2 * lenRatio + 0.3 * mirrorSc;

  return Math.max(0.0, Math.min(1.0, score));
}

// ─── Mirror pairs table ─────────────────────────────────────────────────

function buildMirrorPairsTable(
  skel: Skeleton,
  data: SkeletonSpatialData,
): void {
  const height = data.totalHeight > 1e-6 ? data.totalHeight : 1.0;

  const extent = new Vector3().subVectors(data.maxBounds, data.minBounds);
  let skeletonScale = extent.length();
  if (skeletonScale < 1e-6) skeletonScale = 1.0;

  for (const bp of data.branchingPoints.values()) {
    const parentBone = skel.bone(bp.boneIdx);
    const nonAuxChildren = parentBone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );
    if (nonAuxChildren.length < 2) continue;

    const used = new Set<number>();

    for (let i = 0; i < nonAuxChildren.length; i++) {
      const idxA = nonAuxChildren[i];
      if (used.has(idxA) || data.mirrorPairs.has(idxA)) continue;

      const boneA = skel.bone(idxA);
      const posA = new Vector3().setFromMatrixPosition(boneA.restWorld);
      const vertA = data.getVerticalPosition(idxA);
      const latA = data.getLateralOffset(idxA);
      const lenA = boneA.length;

      let bestMatch: number | null = null;
      let bestScore = 0.0;

      for (let j = i + 1; j < nonAuxChildren.length; j++) {
        const idxB = nonAuxChildren[j];
        if (used.has(idxB) || data.mirrorPairs.has(idxB)) continue;

        const boneB = skel.bone(idxB);
        const posB = new Vector3().setFromMatrixPosition(boneB.restWorld);
        const vertB = data.getVerticalPosition(idxB);
        const latB = data.getLateralOffset(idxB);
        const lenB = boneB.length;

        if (latA * latB >= 0) continue;
        if (Math.abs(vertA - vertB) > 0.02) continue;

        const maxLen = Math.max(lenA, lenB);
        if (maxLen > 1e-6 && Math.abs(lenA - lenB) / maxLen > 0.1) continue;

        const lateralAVal = posA.dot(data.lateralAxis);
        const mirroredA = posA
          .clone()
          .addScaledVector(data.lateralAxis, -2 * lateralAVal);
        const mirrorDist = mirroredA.distanceTo(posB);
        if (mirrorDist > 0.01 * skeletonScale) continue;

        const score = computeSymmetryScore(
          skel,
          idxA,
          idxB,
          data.lateralAxis,
        );
        if (score > 0.9 && score > bestScore) {
          bestScore = score;
          bestMatch = idxB;
        }
      }

      if (bestMatch !== null) {
        data.mirrorPairs.set(idxA, bestMatch);
        data.mirrorPairs.set(bestMatch, idxA);
        data.mirrorScores.set(idxA, bestScore);
        data.mirrorScores.set(bestMatch, bestScore);
        used.add(idxA);
        used.add(bestMatch);
      }
    }
  }
}

// ─── computeSpatialLandmarks ────────────────────────────────────────────

export function computeSpatialLandmarks(
  skel: Skeleton,
  spatialData: SkeletonSpatialData,
): SpatialLandmarks {
  const landmarks = new SpatialLandmarks();

  for (const [boneIdx, bp] of spatialData.branchingPoints) {
    const score = scorePelvisCandidate(boneIdx, bp, spatialData);
    if (score > 0.3) {
      landmarks.pelvisCandidates.push([boneIdx, score]);
    }
  }
  landmarks.pelvisCandidates.sort((a, b) => b[1] - a[1]);
  if (landmarks.pelvisCandidates.length > 0) {
    landmarks.likelyPelvis = landmarks.pelvisCandidates[0][0];
  }

  for (const [boneIdx, bp] of spatialData.branchingPoints) {
    const score = scoreChestCandidate(boneIdx, bp, spatialData);
    if (score > 0.3) {
      landmarks.chestCandidates.push([boneIdx, score]);
    }
  }
  landmarks.chestCandidates.sort((a, b) => b[1] - a[1]);
  if (landmarks.chestCandidates.length > 0) {
    landmarks.likelyChest = landmarks.chestCandidates[0][0];
  }

  for (const [boneIdx, bp] of spatialData.branchingPoints) {
    const score = scoreHandCandidate(boneIdx, bp, spatialData);
    if (score > 0.3) {
      const latOffset = spatialData.getLateralOffset(boneIdx);
      const side = latOffset < 0 ? 'left' : 'right';
      landmarks.handCandidates[side].push([boneIdx, score]);
    }
  }
  for (const side of ['left', 'right'] as const) {
    landmarks.handCandidates[side].sort((a, b) => b[1] - a[1]);
  }
  if (landmarks.handCandidates.left.length > 0) {
    landmarks.likelyLeftHand = landmarks.handCandidates.left[0][0];
  }
  if (landmarks.handCandidates.right.length > 0) {
    landmarks.likelyRightHand = landmarks.handCandidates.right[0][0];
  }

  return landmarks;
}

// ─── Landmark scoring helpers ───────────────────────────────────────────

function scorePelvisCandidate(
  _boneIdx: number,
  bp: BranchingPoint,
  _spatialData: SkeletonSpatialData,
): number {
  let score = 0.0;

  if (!bp.hasDownwardChildren) return 0.0;
  score += 0.25;

  if (bp.hasSymmetricChildren) score += 0.25;

  const centeredScore = 1.0 - Math.min(Math.abs(bp.lateralOffset) / 0.2, 1.0);
  score += 0.25 * centeredScore;

  const vert = bp.verticalPosition;
  if (vert >= 0.2 && vert <= 0.5) {
    const vertScore = 1.0 - Math.abs(vert - 0.35) / 0.15;
    score += 0.25 * Math.max(0, vertScore);
  }

  return Math.min(1.0, score);
}

function scoreChestCandidate(
  _boneIdx: number,
  bp: BranchingPoint,
  _spatialData: SkeletonSpatialData,
): number {
  let score = 0.0;

  if (!bp.hasLateralChildren) return 0.0;
  score += 0.25;

  if (bp.hasSymmetricChildren) score += 0.25;

  const centeredScore =
    1.0 - Math.min(Math.abs(bp.lateralOffset) / 0.15, 1.0);
  score += 0.25 * centeredScore;

  const vert = bp.verticalPosition;
  if (vert >= 0.45 && vert <= 0.8) {
    const vertScore = 1.0 - Math.abs(vert - 0.6) / 0.2;
    score += 0.25 * Math.max(0, vertScore);
  }

  return Math.min(1.0, score);
}

function scoreHandCandidate(
  _boneIdx: number,
  bp: BranchingPoint,
  _spatialData: SkeletonSpatialData,
): number {
  let score = 0.0;

  if (bp.childCount < 3) return 0.0;

  const childScore = Math.min(bp.childCount / 5.0, 1.0);
  score += 0.3 * childScore;

  const lat = Math.abs(bp.lateralOffset);
  if (lat < 0.3) return 0.0;

  const extremityScore = Math.min((lat - 0.3) / 0.5, 1.0);
  score += 0.4 * extremityScore;

  const vert = bp.verticalPosition;
  if (vert >= 0.25 && vert <= 0.75) {
    const vertScore = 1.0 - Math.abs(vert - 0.5) / 0.25;
    score += 0.3 * Math.max(0, vertScore);
  }

  return Math.min(1.0, score);
}

// ─── Chain traversal helpers ────────────────────────────────────────────

export function findUpwardCenteredChain(
  skel: Skeleton,
  startIdx: number,
  spatialData: SkeletonSpatialData,
  options: {
    maxLateralOffset?: number;
    stopAtBranching?: boolean;
    stopAtHighBranching?: boolean;
    useRawXCentering?: boolean;
  } = {},
): number[] {
  const maxLateralOffset = options.maxLateralOffset ?? 0.2;
  const stopAtBranching = options.stopAtBranching ?? true;
  const stopAtHighBranching = options.stopAtHighBranching ?? false;
  const useRawXCentering = options.useRawXCentering ?? false;

  const chain = [startIdx];
  let currentIdx = startIdx;

  let startRawX = 0;
  let centerThreshold = 0;
  if (useRawXCentering) {
    startRawX = skel.bone(startIdx).restWorld.elements[12];
    centerThreshold = spatialData.totalHeight * 0.05;
  }

  while (true) {
    const bone = skel.bone(currentIdx);
    const nonAuxChildren = bone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );

    if (nonAuxChildren.length === 0) break;

    let bestChild: number | null = null;
    let bestScore = -1.0;
    const currentVert = spatialData.getVerticalPosition(currentIdx);

    for (const childIdx of nonAuxChildren) {
      const childVert = spatialData.getVerticalPosition(childIdx);
      if (childVert <= currentVert) continue;

      let centeringScore: number;
      if (useRawXCentering) {
        const childRawX = skel.bone(childIdx).restWorld.elements[12];
        const xOffset = Math.abs(childRawX - startRawX);
        if (xOffset > centerThreshold) continue;
        centeringScore =
          centerThreshold > 0 ? 1.0 - xOffset / centerThreshold : 1.0;
      } else {
        const childLat = Math.abs(spatialData.getLateralOffset(childIdx));
        if (childLat > maxLateralOffset) continue;
        centeringScore = 1.0 - childLat / maxLateralOffset;
      }

      const score = childVert - currentVert + centeringScore;
      if (score > bestScore) {
        bestScore = score;
        bestChild = childIdx;
      }
    }

    if (bestChild === null) break;

    if (stopAtBranching || stopAtHighBranching) {
      const bestChildBone = skel.bone(bestChild);
      const bestChildNonAux = bestChildBone.children.filter(
        (c) => !skel.bone(c).auxiliary,
      );
      const bp = spatialData.getBranchingPoint(bestChild);
      if (bp) {
        if (
          stopAtBranching &&
          bp.hasLateralChildren &&
          bestChildNonAux.length > 1
        )
          break;
        if (stopAtHighBranching && bestChildNonAux.length >= 3) break;
      }
    }

    chain.push(bestChild);
    currentIdx = bestChild;
  }

  return chain;
}

export function findLateralSpreadChain(
  skel: Skeleton,
  startIdx: number,
  spatialData: SkeletonSpatialData,
  direction: 'left' | 'right' = 'left',
  minChainLength = 2,
): number[] {
  const chain = [startIdx];
  let currentIdx = startIdx;

  const dirMult = direction === 'left' ? -1.0 : 1.0;

  const getRawX = (boneIdx: number): number => {
    const pos = new Vector3().setFromMatrixPosition(
      skel.bone(boneIdx).restWorld,
    );
    return pos.dot(spatialData.lateralAxis);
  };

  const extent = new Vector3().subVectors(
    spatialData.maxBounds,
    spatialData.minBounds,
  );
  const lateralExtent = dotAbs(extent, spatialData.lateralAxis);
  const spreadThreshold = lateralExtent * 0.02;

  while (true) {
    const bone = skel.bone(currentIdx);
    const nonAuxChildren = bone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );

    if (nonAuxChildren.length === 0) break;

    let bestChild: number;
    let bestSpread = -Infinity;

    if (nonAuxChildren.length === 1) {
      bestChild = nonAuxChildren[0];
    } else {
      let bestSubtreeScore = -1;
      let foundChild: number | null = null;

      const currentX = getRawX(currentIdx);

      for (const childIdx of nonAuxChildren) {
        const childX = getRawX(childIdx);
        const spread = (childX - currentX) * dirMult;

        let subtreeScore = 0;
        const childBone = skel.bone(childIdx);
        subtreeScore += childBone.subtreeSize;

        const bp = spatialData.getBranchingPoint(childIdx);
        if (bp && bp.childCount >= 3) {
          subtreeScore += 100;
        } else {
          for (const grandchildIdx of childBone.children) {
            const gcBp = spatialData.getBranchingPoint(grandchildIdx);
            if (gcBp && gcBp.childCount >= 3) {
              subtreeScore += 50;
              break;
            }
          }
        }

        if (
          spread > bestSpread ||
          (spread === bestSpread && subtreeScore > bestSubtreeScore)
        ) {
          bestSpread = spread;
          bestSubtreeScore = subtreeScore;
          foundChild = childIdx;
        }
      }

      if (foundChild === null) break;
      bestChild = foundChild;

      if (bestSpread < -spreadThreshold && chain.length >= minChainLength)
        break;
    }

    chain.push(bestChild);

    const bp = spatialData.getBranchingPoint(bestChild);
    if (bp && bp.childCount >= 3) {
      const childBone = skel.bone(bestChild);
      let fingerLikeChildren = 0;
      for (const cIdx of childBone.children) {
        const grandchild = skel.bone(cIdx);
        if (grandchild.auxiliary) continue;
        if (grandchild.children.length > 0) fingerLikeChildren++;
      }
      if (fingerLikeChildren >= 3) break;
    }

    currentIdx = bestChild;
  }

  return chain;
}

export function findFingerChains(
  skel: Skeleton,
  handIdx: number,
  spatialData: SkeletonSpatialData,
  maxFingerLength = 4,
): Record<string, number[]> {
  const handBone = skel.bone(handIdx);
  const nonAuxChildren = handBone.children.filter(
    (c) => !skel.bone(c).auxiliary,
  );

  if (nonAuxChildren.length < 2) return {};

  const fingerChains: number[][] = [];
  const fingerDirections: Vector3[] = [];

  for (const childIdx of nonAuxChildren) {
    const chain = traceLinearChain(skel, childIdx, maxFingerLength);
    if (chain.length >= 2) {
      fingerChains.push(chain);
      fingerDirections.push(skel.bone(childIdx).direction.clone());
    }
  }

  if (fingerChains.length < 2) return {};

  const result: Record<string, number[]> = {};
  const thumbIdx = identifyThumb(fingerDirections);

  if (thumbIdx !== null && fingerChains.length >= 4) {
    const thumbChain = fingerChains.splice(thumbIdx, 1)[0];
    fingerDirections.splice(thumbIdx, 1);
    result.thumb = thumbChain;

    const handLat = spatialData.getLateralOffset(handIdx);
    const isLeft = handLat < 0;

    const sortedChains = [...fingerChains].sort((a, b) => {
      const latA = spatialData.getLateralOffset(a[0]);
      const latB = spatialData.getLateralOffset(b[0]);
      return isLeft ? latB - latA : latA - latB;
    });

    const fingerNames = ['index', 'middle', 'ring', 'pinky'];
    for (let i = 0; i < sortedChains.length; i++) {
      result[i < fingerNames.length ? fingerNames[i] : `finger_${i}`] =
        sortedChains[i];
    }
  } else {
    for (let i = 0; i < fingerChains.length; i++) {
      result[`finger_${i}`] = fingerChains[i];
    }
  }

  return result;
}

function traceLinearChain(
  skel: Skeleton,
  startIdx: number,
  maxLength: number,
): number[] {
  const chain = [startIdx];
  let currentIdx = startIdx;

  while (chain.length < maxLength) {
    const bone = skel.bone(currentIdx);
    const nonAuxChildren = bone.children.filter(
      (c) => !skel.bone(c).auxiliary,
    );

    if (nonAuxChildren.length !== 1) break;

    chain.push(nonAuxChildren[0]);
    currentIdx = nonAuxChildren[0];
  }

  return chain;
}

function identifyThumb(directions: Vector3[]): number | null {
  if (directions.length < 3) return null;

  let bestThumb: number | null = null;
  let bestDeviation = 0.0;

  for (let i = 0; i < directions.length; i++) {
    const dirI = directions[i];
    const avg = new Vector3();
    for (let j = 0; j < directions.length; j++) {
      if (j !== i) avg.add(directions[j]);
    }
    avg.divideScalar(directions.length - 1);
    const avgLen = avg.length();
    if (avgLen > 1e-6) avg.divideScalar(avgLen);

    const dirNorm = dirI.clone();
    const dirLen = dirNorm.length();
    if (dirLen > 1e-6) dirNorm.divideScalar(dirLen);

    const deviation = 1.0 - dirNorm.dot(avg);
    if (deviation > bestDeviation) {
      bestDeviation = deviation;
      bestThumb = i;
    }
  }

  return bestDeviation > 0.3 ? bestThumb : null;
}

export function isClavickleLike(
  skel: Skeleton,
  boneIdx: number,
  spatialData: SkeletonSpatialData,
  nextBoneIdx?: number,
): boolean {
  const bone = skel.bone(boneIdx);

  const vert = spatialData.getVerticalPosition(boneIdx);
  if (vert < 0.4 || vert > 0.95) return false;

  const bonePos = new Vector3().setFromMatrixPosition(bone.restWorld);
  const boneX = Math.abs(bonePos.dot(spatialData.lateralAxis));

  if (nextBoneIdx !== undefined) {
    const nextBone = skel.bone(nextBoneIdx);
    const nextPos = new Vector3().setFromMatrixPosition(nextBone.restWorld);
    const nextX = Math.abs(nextPos.dot(spatialData.lateralAxis));

    if (nextX <= boneX) return false;

    const extent = new Vector3().subVectors(
      spatialData.maxBounds,
      spatialData.minBounds,
    );
    const lateralExtent = dotAbs(extent, spatialData.lateralAxis);
    const minMovement = lateralExtent * 0.05;

    if (nextX - boneX < minMovement) return false;
  }

  const dir = bone.direction;
  if (dir.length() > 1e-6) {
    const latComponent = Math.abs(dir.dot(spatialData.lateralAxis));
    const vertComponent = Math.abs(dir.dot(spatialData.upAxis));
    if (latComponent < vertComponent * 0.5) return false;
  }

  if (nextBoneIdx !== undefined) {
    const nextBone = skel.bone(nextBoneIdx);
    if (bone.length > nextBone.length * 1.5) return false;
  }

  return true;
}

export function getLateralSpread(
  spatialData: SkeletonSpatialData,
  chain: number[],
): number {
  if (chain.length < 2) return 0.0;
  const startLat = spatialData.getLateralOffset(chain[0]);
  const endLat = spatialData.getLateralOffset(chain[chain.length - 1]);
  return Math.abs(endLat - startLat);
}
